import React, { useState } from "react";
import { AlarmClock, CalendarPlus, CheckCircle2, ChevronDown, ChevronUp, ExternalLink, HelpCircle } from "lucide-react";
import { buildReminderIcs, describeTiming, formatReminderDate } from "@/lib/grantReminders";

const URGENCY_STYLES = {
  overdue: "border-red-300 bg-red-50",
  urgent: "border-red-200 bg-red-50/60",
  due: "border-amber-200 bg-amber-50",
  upcoming: "border-slate-200 bg-white",
};
const TIMING_STYLES = {
  overdue: "text-red-800",
  urgent: "text-red-700",
  due: "text-amber-800",
  upcoming: "text-slate-600",
};
const KIND_STYLES = {
  extension: "border-orange-200 bg-orange-100 text-orange-800",
  new_grant: "border-rose-200 bg-rose-100 text-rose-800",
  extension_check: "border-sky-200 bg-sky-100 text-sky-800",
  follow_up: "border-violet-200 bg-violet-100 text-violet-800",
  not_covered: "border-rose-200 bg-rose-100 text-rose-800",
  condition: "border-amber-200 bg-amber-100 text-amber-900",
};

function downloadIcs(reminder) {
  const url = URL.createObjectURL(new Blob([buildReminderIcs(reminder)], { type: "text/calendar" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `${reminder.label}-${reminder.clientName}`.replace(/[^a-z0-9_-]+/gi, "_") + ".ics";
  link.click();
  URL.revokeObjectURL(url);
}

// The list of extensions and new grants due, shown at the top of the diaries and the Aid
// Planner. onMarkApplied clears one (the diaries); onOpen jumps to it (the planner).
// guideHref links to the staff guide to how the reminders work.
export default function GrantRemindersPanel({ reminders = [], onMarkApplied, onOpen, flowchartHref, guideHref, className = "" }) {
  const guideLink = guideHref && (
    <a href={guideHref} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:underline">
      <HelpCircle className="h-3.5 w-3.5" /> How reminders work
    </a>
  );
  const [expanded, setExpanded] = useState(true);
  const pressing = reminders.filter((reminder) => reminder.urgency !== "upcoming").length;

  if (!reminders.length) {
    return (
      <div className={`flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 print:hidden ${className}`}>
        <CheckCircle2 className="h-4 w-4" /> No extensions or new grants are due for the matters shown.
        {guideLink && <span className="ml-auto">{guideLink}</span>}
      </div>
    );
  }

  return (
    <section aria-label="Extensions and new grants to apply for" className={`rounded-xl border border-orange-200 bg-white shadow-sm print:hidden ${className}`}>
      <button type="button" onClick={() => setExpanded((open) => !open)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <span className="flex items-center gap-2">
          <AlarmClock className="h-5 w-5 text-orange-600" />
          <span className="font-bold text-slate-900">Extensions &amp; new grants to apply for</span>
          <span className="rounded-full bg-orange-600 px-2 py-0.5 text-xs font-bold text-white">{reminders.length}</span>
          {pressing > 0 && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-800">{pressing} due now</span>}
        </span>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {expanded && (
        <div className="border-t border-orange-100 px-4 pb-4 pt-3">
          <p className="mb-3 text-xs text-slate-500">
            Apply at least two weeks before the listing. VLA will not pay for work done before the grant or extension covers it.
            {flowchartHref && <> <a href={flowchartHref} className="inline-flex items-center gap-0.5 font-semibold text-blue-700 hover:underline">Not sure? Use the Aid Extension Flowchart <ExternalLink className="h-3 w-3" /></a></>}
            {guideLink && <> {guideLink}</>}
          </p>
          <ul className="space-y-2">
            {reminders.map((reminder) => (
              <li key={reminder.id} className={`flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between ${URGENCY_STYLES[reminder.urgency]}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-slate-900">{reminder.clientName}</span>
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${KIND_STYLES[reminder.kind]}`}>{reminder.label}</span>
                    {reminder.lawyer && <span className="text-xs font-semibold text-slate-500">{reminder.lawyer}</span>}
                  </div>
                  <p className={`mt-1 text-sm font-semibold ${TIMING_STYLES[reminder.urgency]}`}>{describeTiming(reminder)}</p>
                  <p className="mt-0.5 text-xs text-slate-600">
                    {[reminder.court, reminder.appearanceType, reminder.listingDate && `listed ${formatReminderDate(reminder.listingDate)}`].filter(Boolean).join(" · ")}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">{reminder.reason}</p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <button type="button" onClick={() => downloadIcs(reminder)} title="Add an apply-by reminder to Outlook" className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:border-slate-400">
                    <CalendarPlus className="h-3.5 w-3.5" /> Add to Outlook
                  </button>
                  {onOpen && (
                    <button type="button" onClick={() => onOpen(reminder)} className="rounded-lg border border-purple-200 bg-purple-50 px-2.5 py-1.5 text-xs font-bold text-purple-800 hover:bg-purple-100">
                      Open matter
                    </button>
                  )}
                  {onMarkApplied && (
                    <button type="button" onClick={() => onMarkApplied(reminder)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {reminder.doneLabel || "Mark as applied"}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
